/* eslint camelcase: 0 */
import moment from 'moment'
import { DateTime } from 'luxon'
import { IMatcherItem, IIssue } from '@/features/matchFields/store'

import {
  ERROR,
  WARNING
} from '@/constants'
import { matchFieldsLocalisationKeys } from '@/features/matchFields/matchFields.localisation'

interface ITimeFormats {
  time_start: string
  time_end: string
  timestamp_start: string
  timestamp_end: string
  date_start: string
  date_end: string
}

interface IValidationMatchFields {
  matcher: any
  matcherForDisplay: any
  dateTimeFormat: string
}

interface IRequiredProps {
  rooms: string
}

export class ValidationService {
  public mappedValue: any
  public mappedValueForDisplay: any
  private formats: any
  private dateWithFormats: Record<string, DateTime> = {}

  private readonly requiredProps: IRequiredProps = {
    rooms: 'Room name'
  }

  private readonly timeProps: string[] = [
    'time_start',
    'time_end'
  ]

  private readonly timestampProps: string[] = [
    'timestamp_start',
    'timestamp_end'
  ]

  private readonly dateProps: string[] = [
    'date_start',
    'date_end'
  ]

  private readonly maxStringLength: number = 256
  private readonly minTz: number = -12
  private readonly maxTz: number = 12

  public validate ({ matcher, matcherForDisplay, dateTimeFormat }: IValidationMatchFields) {
    this.mappedValue = matcher
    this.mappedValueForDisplay = matcherForDisplay

    const [dateFormat, timeFormat] = dateTimeFormat.split('\'T\'')
    this.formats = {
      timestamp_start: dateTimeFormat,
      timestamp_end: dateTimeFormat,
      date_start: dateFormat,
      date: dateFormat,
      date_end: dateFormat,
      time_start: timeFormat,
      time_end: timeFormat
    }

    const { timezone } = this.mappedValue
    const issues = []

    if (!timezone) issues.push({ type: WARNING, message: matchFieldsLocalisationKeys.TIMEZONE_EMPTY })

    const checkIsValidStringErrors = this.checkIsValidString()
    const checkRequiredFieldsErrors = this.checkRequiredFields()
    const validateTimeValuesErrors = this.validateTimeValues()
    const tzErrors = this.checkTimezone()

    return {
      issues: [
        ...issues,
        ...checkIsValidStringErrors,
        ...checkRequiredFieldsErrors,
        ...validateTimeValuesErrors,
        ...tzErrors
      ]
    }
  }

  private validateTimeValues (): IIssue[] {
    const {
      timestamp_start,
      timestamp_end,
      date,
      date_start = date,
      date_end = date,
      time_start,
      time_end
    } = this.mappedValue
    const issues = []

    if (!timestamp_start && !((date_start || date) && time_start)) {
      issues.push(this.createError(matchFieldsLocalisationKeys.TIME_START_DATE_START_REQUIRED))
    }

    if (!timestamp_end && !((date_end || date) && time_end)) {
      issues.push(this.createError(matchFieldsLocalisationKeys.TIME_END_DATE_END_REQUIRED))
    }

    if (timestamp_start || timestamp_end) {
      if (date_start || time_start || date_end || time_end) {
        issues.push(this.createError(matchFieldsLocalisationKeys.ONE_VERSION_TIME_AND_DATE))
      }
    }

    const notValidTimestampErrors = this.checkIsValidTimestamp()

    const startAfterEndErrors = this.checkIsTheSameOrAfter(timestamp_start, timestamp_end)

    const startDateAfterEndDateErrors = this.checkIsTheSameOrAfter(date_start, date_end, time_start, time_end)

    return [
      ...issues,
      ...notValidTimestampErrors,
      ...startAfterEndErrors,
      ...startDateAfterEndDateErrors
    ]
  }

  private checkIsValidTimestamp(): IIssue[] {
    return this.timeProps
        .concat(this.dateProps)
        .concat(this.timestampProps)
        .reduce((acc: IIssue[], dateTimeKey: string) => {
          const timeFields = this.mappedValue[dateTimeKey]
          const timeValue = this.mappedValueForDisplay.find(
              ({ column }: IMatcherItem) => column === timeFields
          )

          if (!timeValue?.sampleValue || !this.formats[dateTimeKey]) {
            return acc
          }

          const parsed = DateTime.fromFormat(
              timeValue.sampleValue.toString(),
              this.formats[dateTimeKey]
          )

          if (!parsed.isValid) {
            acc.push(this.createError(matchFieldsLocalisationKeys.NOT_VALID_TIME_COLUMNS))
          } else {
            this.dateWithFormats[timeValue.column] = parsed
          }

          return acc
        }, [])
  }

  private checkIsTheSameOrAfter (start: string, end: string, time_start?: string, time_end?: string): IIssue[] {
    if (start && end) {
      const momentStart: moment.Moment = moment(this.dateWithFormats[start])
      const momentEnd: moment.Moment = moment(this.dateWithFormats[end])

      let isAfter: boolean = momentEnd.isSameOrAfter(momentStart)

      if (!isAfter && time_start && time_end) {
        const momentTimeStart: moment.Moment = moment(this.dateWithFormats[time_start])
        const momentTimeEnd: moment.Moment = moment(this.dateWithFormats[time_end])
        isAfter = momentEnd.isSame(momentStart)

        if (isAfter && !momentTimeEnd.isSameOrAfter(momentTimeStart)) return [this.createError(matchFieldsLocalisationKeys.MORE_THAN_START)]
      }

      if (!isAfter) {
        return [this.createError(matchFieldsLocalisationKeys.MORE_THAN_START)]
      }
    }
    return []
  }

  private checkRequiredFields (): IIssue[] {
    return Object.entries(this.requiredProps).reduce((acc: IIssue[], [k, v]: [string, string]) => {
      if (!this.mappedValue[k] || !this.mappedValue[k].length) {
        acc.push(this.createError(`${v} is required`))
      }
      return acc
    }, [])
  }

  private checkIsValidString (): IIssue[] {
    return this.mappedValueForDisplay.reduce((acc: IIssue[], { sampleValue }: IMatcherItem) => {
      if (typeof sampleValue === 'string' && sampleValue.length >= this.maxStringLength) {
        acc.push(this.createError(matchFieldsLocalisationKeys.NOT_VALID_STRING))
      }
      return acc
    }, [])
  }

  private checkTimezone (): IIssue[] {
    const { timezone } = this.mappedValue
    let tz: string = ''

    if (!timezone) return []

    tz = this.mappedValueForDisplay.find(({ column, sampleValue }: { column: string, sampleValue: string }) => {
      if (column === timezone) return sampleValue
    })

    const numericTz = Number(tz)
    if (
      Number.isSafeInteger(numericTz) &&
      (
        numericTz < this.minTz &&
        numericTz > this.maxTz
      )
    ) return [this.createError(matchFieldsLocalisationKeys.NOT_VALID_TIMEZONE)]

    return []
  }

  private createError (message: string): IIssue {
    return {
      type: ERROR,
      message
    }
  }
}
